import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { LoginButtons } from '@/components/auth/LoginButtons';
import { EmailLoginForm } from '@/components/auth/EmailLoginForm';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui';

type LoginMethod = 'social' | 'email';

export default function Login() {
  const { t } = useTranslation();
  const { firebaseUser, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('social');

  // Capturar franchiseId de la URL
  const franchiseIdFromUrl = searchParams.get('franchiseId');

  useEffect(() => {
    // Solo navegar cuando la carga esté completa y el usuario esté autenticado
    // Esto evita la navegación prematura mientras se cargan los datos del usuario
    // replace: true — si no, /login queda en el historial y el botón atrás de
    // Android rebota aquí y vuelve a empujar a /dashboard en bucle (no se puede salir)
    if (!loading && firebaseUser) {
      navigate('/dashboard', { replace: true });
    }
  }, [firebaseUser, loading, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas p-4">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">{t('auth.loginPage.title')}</CardTitle>
          <p className="text-center text-sm text-ink-muted mt-2">
            {t('auth.loginPage.subtitle')}
          </p>
        </CardHeader>
        <CardContent className="flex flex-col items-center">
          {/* Tabs para elegir método de login */}
          <div className="flex gap-2 mb-6 w-full max-w-sm bg-surface-2 p-1 rounded-lg">
            <button
              onClick={() => setLoginMethod('social')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                loginMethod === 'social'
                  ? 'bg-surface text-ink shadow-sm'
                  : 'text-ink-muted hover:text-ink'
              }`}
            >
              {t('auth.loginPage.socialTab')}
            </button>
            <button
              onClick={() => setLoginMethod('email')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                loginMethod === 'email'
                  ? 'bg-surface text-ink shadow-sm'
                  : 'text-ink-muted hover:text-ink'
              }`}
            >
              {t('auth.loginPage.emailTab')}
            </button>
          </div>

          {/* Contenido según método seleccionado */}
          {loginMethod === 'social' ? (
            <LoginButtons franchiseId={franchiseIdFromUrl} />
          ) : (
            <EmailLoginForm franchiseId={franchiseIdFromUrl} />
          )}

          <p className="text-xs text-ink-subtle text-center mt-6">
            {t('auth.loginPage.byContinuing')}{' '}
            <a href="/terms-of-service" className="text-ink-muted underline hover:text-ink">
              {t('auth.loginPage.termsOfService')}
            </a>{' '}
            {t('auth.loginPage.and')}{' '}
            <a href="/privacy-policy" className="text-ink-muted underline hover:text-ink">
              {t('auth.loginPage.privacyPolicy')}
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
